import { ws_base_url } from 'django-rest-react';

export interface PresenceState {
  status: 'connecting' | 'connected' | 'disconnected' | 'unavailable';
  participants: number[];
}

// Both presence endpoints use the same snapshots and heartbeat protocol.
export const handlePresenceMessage = (message: any, ws: WebSocket, setPeople: (people: number[]) => void) => {
  if (message?.type === 'connected_people') {
    if (Array.isArray(message.partecipanti)) {
      setPeople([...new Set<number>(message.partecipanti.filter((id: unknown) =>
        typeof id === 'number' && Number.isInteger(id) && id > 0))]);
    }
    return true;
  }
  if (message?.type === 'ping') {
    ws.send(JSON.stringify({ type: 'im_connected' }));
    return true;
  }
  return false;
};

export const connectOnlinePresence = (onChange: (state: PresenceState) => void) => {
  let socket: WebSocket | null = null;
  let reconnectTimer: number | undefined;
  let heartbeatDeadline: number | undefined;
  let stopped = false;
  let retryDelay = 5000;

  const reconnect = () => {
    window.clearTimeout(heartbeatDeadline);
    onChange({ status: 'disconnected', participants: [] });
    reconnectTimer = window.setTimeout(connect, retryDelay);
    retryDelay = Math.min(retryDelay * 2, 30000);
  };

  const waitForHeartbeat = (ws: WebSocket) => {
    window.clearTimeout(heartbeatDeadline);
    heartbeatDeadline = window.setTimeout(() => {
      if (stopped || socket !== ws) return;
      socket = null;
      ws.close();
      reconnect();
    }, 90000);
  };

  const connect = () => {
    if (stopped) return;
    onChange({ status: 'connecting', participants: [] });
    const ws = new WebSocket(`${ws_base_url()}/ws/online/`);
    socket = ws;
    waitForHeartbeat(ws);
    ws.onmessage = event => {
      if (stopped || socket !== ws) return;
      waitForHeartbeat(ws);
      let message;
      try {
        message = JSON.parse(event.data);
      } catch {
        return;
      }
      if (message?.type === 'error') {
        // Authentication/permission failures need a new login, not retries.
        stopped = true;
        window.clearTimeout(heartbeatDeadline);
        onChange({ status: 'unavailable', participants: [] });
        ws.close();
        return;
      }
      handlePresenceMessage(message, ws, participants => {
        retryDelay = 5000;
        onChange({ status: 'connected', participants });
      });
    };
    ws.onclose = () => {
      if (stopped || socket !== ws) return;
      socket = null;
      reconnect();
    };
  };

  connect();
  return () => {
    stopped = true;
    window.clearTimeout(reconnectTimer);
    window.clearTimeout(heartbeatDeadline);
    socket?.close();
  };
};
